import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import SuperSimpleStickerCard from './SuperSimpleStickerCard';
import TagFilter from './TagFilter';
import TagChip from './TagChip';
import ClothedEyeLoader from './ClothedEyeLoader';
import databaseService from '../services/databaseService';
import { trackSearchFilter, trackStickerEngagement } from '../services/analyticsService';
import logger from '../utils/logger.js';
import './HomePageSidebar.css';

const HomePageSidebar = ({
  onAddToScene,
  collapsed = false,
  onRefreshRef,
  onLoadingStateChange,
}) => {
  // State for listings and loading
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [pagination, setPagination] = useState({ page: 1, limit: 50 });
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // State for filtering and sorting (NO PERSISTENCE - fresh state every time)
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState('sales'); // Default to sales for popularity-based ordering
  const [sortOrder, setSortOrder] = useState('desc');
  const [groupByCategory, setGroupByCategory] = useState(false); // Default to ungrouped
  const [userGroupByCategory, setUserGroupByCategory] = useState(false); // User's preference defaults to ungrouped

  // Tag filter state (NO PERSISTENCE)
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Search input ref
  const searchInputRef = useRef(null);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(loading || loadingMore);
    }
  }, [loading, loadingMore, onLoadingStateChange]);

  // Debounced search state
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Auto-ungroup when filters are applied
  useEffect(() => {
    const hasFilters = filterText.trim() !== '' || selectedTags.length > 0;

    if (hasFilters) {
      // Filters are active - force ungrouping for better layout
      setGroupByCategory(false);
    } else {
      // No filters - restore user's grouping preference
      setGroupByCategory(userGroupByCategory);
    }
  }, [filterText, selectedTags, userGroupByCategory]);

  // Load tags from database once on component mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tagData = await databaseService.getAllTags();
        setAllTags(tagData);
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };

    loadTags();
  }, []);

  // Load paginated listings
  const loadListings = useCallback(
    async (
      page = 1,
      search = '',
      sortBy = 'category',
      sortOrder = 'asc',
      category = '',
      append = false
    ) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);

        // Only show listings with svgPath for scene builder
        const result = await databaseService.getUnifiedStickers({
          page,
          limit: 50,
          search,
          sortBy,
          sortOrder,
          category: category !== 'All' ? category : undefined,
          hasSvgPath: true,
        });

        if (result.data) {
          // Only include listings that are COMPLETE for Scene Builder
          const validStickers = result.data.filter((sticker) => {
            // Helper: safe JSON parse
            const safeJsonParse = (jsonString) => {
              if (!jsonString) return {};
              try {
                const cleanedJson = jsonString.replace(/[\x00-\x1F\x7F]/g, '');
                return JSON.parse(cleanedJson);
              } catch (error) {
                return {};
              }
            };

            // Helper: normalize size label to base token
            const normalizeSizeName = (sizeName) => {
              if (!sizeName) return '';
              const first = String(sizeName).trim().split(/\s+/)[0].toUpperCase();
              const map = { MED: 'MEDIUM', LAR: 'LARGE', JUM: 'JUMBO', MAX: 'JUMBO' };
              return map[first] || first;
            };

            // 1) Must have svgPath(s)
            if (
              !sticker.svgPath &&
              !(sticker.isSet && Array.isArray(sticker.svgPaths) && sticker.svgPaths.length > 0)
            ) {
              logger.log(`🚫 Filtering out "${sticker.title}" - missing svgPath(s)`);
              return false;
            }

            // 2) Parse dimensions map
            let dims = {};
            if (sticker.dimensions) {
              dims =
                typeof sticker.dimensions === 'string'
                  ? safeJsonParse(sticker.dimensions)
                  : sticker.dimensions || {};
            }

            // Also allow dimensions to come from sizes array if present (convert to map)
            if ((!dims || Object.keys(dims).length === 0) && sticker.sizes) {
              try {
                const sizesArr = Array.isArray(sticker.sizes)
                  ? sticker.sizes
                  : safeJsonParse(sticker.sizes);
                if (Array.isArray(sizesArr)) {
                  sizesArr.forEach((s) => {
                    if (s && s.name && s.width && s.height) {
                      const base = normalizeSizeName(s.name);
                      dims[base] = { width: parseFloat(s.width), height: parseFloat(s.height) };
                    }
                  });
                }
              } catch (_) {}
            }

            if (!dims || Object.keys(dims).length === 0) return false;

            // 3) Determine expected sizes
            let expectedSizes = [];
            if (sticker.sizes) {
              try {
                const sizesArr = Array.isArray(sticker.sizes)
                  ? sticker.sizes
                  : safeJsonParse(sticker.sizes);
                if (Array.isArray(sizesArr)) {
                  expectedSizes = sizesArr
                    .filter((s) => s && s.enabled === true)
                    .map((s) => normalizeSizeName(s.name))
                    .filter(Boolean);
                }
              } catch (_) {}
            }
            if (expectedSizes.length === 0 && sticker.available_sizes) {
              try {
                const arr = Array.isArray(sticker.available_sizes)
                  ? sticker.available_sizes
                  : JSON.parse(sticker.available_sizes);
                if (Array.isArray(arr)) expectedSizes = arr.map(normalizeSizeName).filter(Boolean);
              } catch (_) {}
            }

            // NO FALLBACKS! If we can't determine expected sizes, reject the sticker
            if (expectedSizes.length === 0) {
              logger.log(`🚫 Filtering out "${sticker.title}" - cannot determine expected sizes`);
              return false;
            }

            // 4) Validate dimensions for all sizes and all designs (if set)
            // Allow keys to be either exact (SIZE or SIZE_designX) or stored as numbers/strings
            const setQty = parseInt(sticker.setQuantity, 10) || 1;
            const requireDesigns = sticker.isSet && setQty > 1;

            const findDim = (obj, key) => {
              if (!obj) return null;
              // Normalize target to base token using same mapping as sizes
              const target = normalizeSizeName(key);
              // Try direct key
              if (obj[target]) return obj[target];
              if (obj[String(target).toUpperCase()]) return obj[String(target).toUpperCase()];
              // Tolerant scan: match by normalized base across keys
              const matchKey = Object.keys(obj).find((k0) => normalizeSizeName(k0) === target);
              if (matchKey) return obj[matchKey];

              // FALLBACK: For single-size listings, try generic dimension keys
              if (obj['SIZE']) return obj['SIZE'];
              if (obj['DEFAULT']) return obj['DEFAULT'];

              return null;
            };

            const hasAllDimensions = expectedSizes.every((size) => {
              const main = findDim(dims, size);
              const okMain = !!(
                main &&
                !isNaN(parseFloat(main.width)) &&
                !isNaN(parseFloat(main.height))
              );
              if (!okMain) return false;
              if (!requireDesigns) return true;
              for (let i = 2; i <= setQty; i++) {
                const d = findDim(dims, `${size}_design${i}`);
                const ok = !!(d && !isNaN(parseFloat(d.width)) && !isNaN(parseFloat(d.height)));
                if (!ok) return false;
              }
              return true;
            });

            if (!hasAllDimensions) return false;

            // 5) For SETs, ensure svgPaths count matches
            if (requireDesigns) {
              const pathsOk =
                Array.isArray(sticker.svgPaths) && sticker.svgPaths.length >= setQty - 1;
              if (!pathsOk) return false;
            }

            return true;
          });

          if (append) {
            setListings((prev) => {
              // Deduplicate by etsyListingId to prevent duplicate keys
              const existingIds = new Set(prev.map((s) => s.etsyListingId));
              const newStickers = validStickers.filter((s) => !existingIds.has(s.etsyListingId));
              return [...prev, ...newStickers];
            });
          } else {
            setListings(validStickers);
          }

          // Update pagination info
          if (result.pagination) {
            setHasMore(result.pagination.hasNext || false);
          } else {
            setHasMore(result.data.length === 50); // Assume there's more if we got a full page
          }
        } else {
          logger.warn('⚠️ No data in response:', result);
          if (!append) {
            setListings([]);
          }
          setHasMore(false);
        }
      } catch (error) {
        console.error('❌ Error loading listings:', error);
        setError(error.message);

        // Fallback: try to load without search if search failed
        if (search && !append) {
          logger.log('🔄 Trying fallback load without search...');
          try {
            const fallbackResult = await databaseService.getUnifiedStickers({
              page,
              limit: 50,
              search: '',
              sortBy,
              sortOrder,
              category: category !== 'All' ? category : undefined,
              hasSvgPath: true,
            });
            if (fallbackResult.data) {
              // Apply the same dimension validation filtering to fallback data
              const validFallbackStickers = fallbackResult.data.filter((sticker) => {
                // Helper function to safely parse JSON strings with control characters
                const safeJsonParse = (jsonString) => {
                  if (!jsonString) return {};
                  try {
                    const cleanedJson = jsonString.replace(/[\x00-\x1F\x7F]/g, '');
                    return JSON.parse(cleanedJson);
                  } catch (error) {
                    return {};
                  }
                };

                let hasValidDimensions = false;

                // Check for dimensions in the unified data
                if (sticker.dimensions) {
                  try {
                    const storedDimensions =
                      typeof sticker.dimensions === 'string'
                        ? safeJsonParse(sticker.dimensions)
                        : sticker.dimensions;
                    if (storedDimensions && Object.keys(storedDimensions).length > 0) {
                      // Check if any dimension has valid width/height
                      const hasValidSizeData = Object.values(storedDimensions).some(
                        (dim) =>
                          dim &&
                          dim.width &&
                          dim.height &&
                          !isNaN(parseFloat(dim.width)) &&
                          !isNaN(parseFloat(dim.height))
                      );
                      if (hasValidSizeData) {
                        hasValidDimensions = true;
                      }
                    }
                  } catch (error) {
                    // Failed to parse dimensions
                  }
                }

                // Check for dimensions in sizes field
                if (!hasValidDimensions && sticker.sizes) {
                  try {
                    const sizesData =
                      typeof sticker.sizes === 'string'
                        ? safeJsonParse(sticker.sizes)
                        : sticker.sizes;
                    if (
                      Array.isArray(sizesData) &&
                      sizesData.some(
                        (s) =>
                          s.width &&
                          s.height &&
                          !isNaN(parseFloat(s.width)) &&
                          !isNaN(parseFloat(s.height))
                      )
                    ) {
                      hasValidDimensions = true;
                    }
                  } catch (error) {
                    // Failed to parse sizes
                  }
                }

                if (!hasValidDimensions) {
                  logger.log(
                    `🚫 Filtering out fallback sticker "${sticker.title}" - no valid dimensions found`
                  );
                  return false;
                }

                return true;
              });

              setListings(validFallbackStickers);
              setHasMore(fallbackResult.pagination?.hasNext || fallbackResult.data.length === 50);
              setError('Search failed, showing all listings');
            }
          } catch (fallbackError) {
            console.error('❌ Fallback load also failed:', fallbackError);
            setListings([]);
            setHasMore(false);
          }
        } else {
          if (!append) {
            setListings([]);
          }
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Refresh function to reload data
  const refreshData = useCallback(() => {
    logger.log('🔄 Refreshing sidebar data...');
    setListings([]);
    setPagination((prev) => ({ ...prev, page: 1, hasMore: true }));
    loadListings(1, filterText, sortBy, sortOrder);

    // Also reload tags
    const loadTags = async () => {
      try {
        const tagData = await databaseService.getAllTags();
        setAllTags(tagData || []);
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };
    loadTags();
  }, [filterText, sortBy, sortOrder, loadListings]);

  // Expose refresh function to parent component
  useEffect(() => {
    if (onRefreshRef) {
      onRefreshRef.current = refreshData;
    }
  }, [refreshData, onRefreshRef]);

  // Initial load
  useEffect(() => {
    loadListings();
  }, [loadListings]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterText);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [filterText]);

  // Load listings when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      setPagination((prev) => ({ ...prev, page: 1 }));
      loadListings(1, debouncedSearch, sortBy, sortOrder);
    }
  }, [debouncedSearch, sortBy, sortOrder, loadListings]);

  // Handle search
  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setFilterText(searchTerm);
    // Track search for analytics
    if (searchTerm.trim()) {
      trackSearchFilter('text_search', searchTerm, listings.length);
    }
    // The debounced effect will handle the API call
  };

  // Handle sort
  const handleSort = (e) => {
    const newSortBy = e.target.value;
    setSortBy(newSortBy);
    setPagination((prev) => ({ ...prev, page: 1 }));
    // Track sort change for analytics
    trackSearchFilter('sort', newSortBy, listings.length);
    loadListings(1, debouncedSearch, newSortBy, sortOrder);
  };

  // Handle sort order
  const handleSortOrder = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
    setPagination((prev) => ({ ...prev, page: 1 }));
    // Track sort order change for analytics
    trackSearchFilter('sort_order', newSortOrder, listings.length);
    loadListings(1, debouncedSearch, sortBy, newSortOrder);
  };

  // Client-side tag and text filtering (parity with homepage)
  const filteredListings = useMemo(() => {
    let result = listings;
    const term = (debouncedSearch || '').trim().toLowerCase();

    if (term) {
      result = result.filter((listing) => {
        const title = (listing.title || '').toLowerCase();
        const description = (listing.description || '').toLowerCase();
        const category = (listing.category || '').toLowerCase();
        const tags = Array.isArray(listing.tags)
          ? listing.tags.map((t) => (t || '').toLowerCase())
          : [];
        // Optional SKUs if present in listing_data
        let skus = [];
        try {
          if (listing.listing_data && typeof listing.listing_data === 'string') {
            const ld = JSON.parse(listing.listing_data);
            if (Array.isArray(ld.skus)) skus = ld.skus.map((s) => (s || '').toLowerCase());
          }
        } catch (_) {}

        return (
          title.includes(term) ||
          description.includes(term) ||
          category.includes(term) ||
          tags.some((t) => t.includes(term)) ||
          skus.some((s) => s.includes(term))
        );
      });
    }

    if (selectedTags.length === 0) {
      return result;
    }

    return result.filter((listing) => {
      if (!listing.tags || !Array.isArray(listing.tags)) return false;
      const listingTagNames = listing.tags.map((tag) => tag.toLowerCase());
      return selectedTags.some((selectedTag) =>
        listingTagNames.includes(selectedTag.toLowerCase())
      );
    });
  }, [listings, selectedTags, debouncedSearch]);

  // Handle add to scene
  const handleAddToScene = (stickerData) => {
    logger.log('➕ Adding sticker to scene from sidebar:', stickerData);

    // Track add to scene action for analytics
    trackStickerEngagement(
      stickerData.listingId,
      'add_sticker_to_scene_from_sidebar',
      stickerData.category,
      stickerData.size,
      stickerData.title
    );

    if (onAddToScene) {
      onAddToScene(stickerData);
    }
  };

  // Handle tag selection changes
  const handleTagsChange = (newSelectedTags) => {
    setSelectedTags(newSelectedTags);
    // Track tag filtering for analytics
    if (newSelectedTags.length > 0) {
      trackSearchFilter('tag_filter', newSelectedTags.join(', '), listings.length);
    }
  };

  // Handle pagination
  const handleLoadMore = () => {
    const nextPage = pagination.page + 1;
    setPagination((prev) => ({ ...prev, page: nextPage }));

    // Track pagination for analytics
    trackSearchFilter('pagination', `page_${nextPage}`, listings.length);

    loadListings(nextPage, debouncedSearch, sortBy, sortOrder, true);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setPagination((prev) => ({ ...prev, page: 1 }));

    // Track category change for analytics
    trackSearchFilter('category_change', category, listings.length);

    loadListings(1, debouncedSearch, sortBy, sortOrder, false, category);
  };

  // Handle group by category toggle
  const handleGroupByCategoryToggle = () => {
    const newGroupByCategory = !groupByCategory;
    setGroupByCategory(newGroupByCategory);
    setUserGroupByCategory(newGroupByCategory);

    // Track grouping toggle for analytics
    trackSearchFilter(
      'group_by_category',
      newGroupByCategory ? 'enabled' : 'disabled',
      listings.length
    );
  };

  // Handle card click - add to scene
  const handleCardClick = (listing) => {
    logger.log('🖱️ CARD CLICKED', {
      listingId: listing.etsyListingId,
      title: listing.title,
      sizes: listing.sizes,
    });
    logger.log('🔍 FULL LISTING OBJECT:', listing);
    logger.log('🔍 IS SET CHECK:', {
      isSet: listing.isSet,
      hasSvgPath: !!listing.svgPath,
      hasSvgPaths: !!(listing.svgPaths && listing.svgPaths.length > 0),
      willTakeSETBranch:
        listing.isSet && (listing.svgPath || (listing.svgPaths && listing.svgPaths.length > 0)),
    });

    // Check if this is a SET listing (multiple designs)
    if (listing.isSet && (listing.svgPath || (listing.svgPaths && listing.svgPaths.length > 0))) {
      logger.log('📦 TAKING SET PACK BRANCH');
      // Handle SET pack (multiple different designs)
      const packStickers = [];

      // Add main design (Design 1) using main svgPath
      if (listing.svgPath) {
        packStickers.push({
          listingId: listing.etsyListingId,
          title: `${listing.title} - Main Design`,
          size: 'MICRO',
          color: 'Black (Matte)',
          image: listing.svgPath,
          etsyUrl: listing.etsy_url,
          svgPath: listing.svgPath,
          // Pass through all the listing data needed for proper size/dimension handling
          availableSizes: listing.available_sizes || [],
          availableColors: listing.available_colors || [],
          dimensions: listing.dimensions,
          sizes: listing.sizes, // 🎯 CRITICAL: Pass sizes for pack logic
          // 🎯 CRITICAL: Use the ACTUAL Etsy data fields that exist
          listing_data: listing.listing_data || null,
          inventory_data: listing.inventory_data || null,
          category: listing.category,
          isSet: listing.isSet,
          setQuantity: listing.setQuantity,
          svgPaths: listing.svgPaths,
          svgTitles: listing.svgTitles,
        });
      }

      // Add additional designs (Design 2, 3, etc.) using svgPaths
      if (listing.svgPaths && listing.svgPaths.length > 0) {
        listing.svgPaths.forEach((path, index) => {
          packStickers.push({
            listingId: listing.etsyListingId,
            title: `${listing.title} - ${listing.svgTitles[index] || `Design ${index + 2}`}`,
            size: 'MICRO',
            color: 'Black (Matte)',
            image: path,
            etsyUrl: listing.etsy_url,
            svgPath: path,
            // Pass through all the listing data needed for proper size/dimension handling
            availableSizes: listing.available_sizes || [],
            availableColors: listing.available_colors || [],
            dimensions: listing.dimensions,
            sizes: listing.sizes, // 🎯 CRITICAL: Pass sizes for pack logic
            // 🎯 CRITICAL: Use the ACTUAL Etsy data fields that exist
            listing_data: listing.listing_data || null,
            inventory_data: listing.inventory_data || null,
            category: listing.category,
            isSet: listing.isSet,
            setQuantity: listing.setQuantity,
            svgPaths: listing.svgPaths,
            svgTitles: listing.svgTitles,
          });
        });
      }

      // Send pack batch to scene
      if (onAddToScene && packStickers.length > 0) {
        const packData = {
          isSetBatch: true,
          packStickers: packStickers,
          listingId: listing.etsyListingId, // 🚨 FIX: Add listingId
          title: listing.title,
          packId: `${listing.title}-MICRO-Black`,
          multipackQuantity: packStickers.length,
          isSet: true,
          // 🚨 FIX: Pass sizes data for pack detection
          sizes: listing.sizes,
          dimensions: listing.dimensions,
          availableSizes: listing.available_sizes || [],
          availableColors: listing.available_colors || [],
        };
        onAddToScene(packData);
      }
    } else {
      logger.log('🎯 TAKING SINGLE STICKER BRANCH');
      // Handle single sticker
      const stickerData = {
        listingId: listing.etsyListingId,
        title: listing.title,
        size: 'MICRO',
        color: 'Black (Matte)',
        image: listing.svgPath || listing.largest_size_image_url || listing.first_image_url,
        etsyUrl: listing.etsy_url,
        // Pass through all the listing data needed for proper size/dimension handling
        availableSizes: listing.available_sizes || [],
        availableColors: listing.available_colors || [],
        dimensions: listing.dimensions,
        sizes: listing.sizes, // 🎯 CRITICAL: Pass sizes for pack logic
        // 🎯 CRITICAL: Use the ACTUAL Etsy data fields that exist
        listing_data: listing.listing_data || null,
        inventory_data: listing.inventory_data || null,
        category: listing.category,
      };

      logger.log('🔍 STICKER DATA BEFORE SENDING:', stickerData);

      if (onAddToScene) {
        onAddToScene(stickerData);
      }
    }
  };

  // Group listings by category if enabled
  const groupedListings = useMemo(() => {
    if (!groupByCategory) return null;

    const groups = {};
    filteredListings.forEach((listing) => {
      const category = listing.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(listing);
    });

    logger.log('📂 Available categories:', Object.keys(groups));

    // Custom sort order to put dinosaurs first
    const categoryOrder = [
      'dinosaurs', // Put dinosaurs first
      'animals',
      'vehicles',
      'cartoon',
      'cryptids-legends',
      'horror',
      'pop-culture',
      'space-sci-fi',
      'outdoor-nature',
      'police-fire-military',
      'holiday-seasonal',
      'phrases-slogans',
      'weird-miscellaneous',
    ];

    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      const aIndex = categoryOrder.indexOf(a.toLowerCase());
      const bIndex = categoryOrder.indexOf(b.toLowerCase());

      logger.log(`🔍 Comparing categories: "${a}" (index: ${aIndex}) vs "${b}" (index: ${bIndex})`);

      // If both categories are in our custom order, sort by that order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one is in our custom order, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // Otherwise, sort alphabetically
      return a.localeCompare(b);
    });

    logger.log(
      '📂 Final category order:',
      sortedGroups.map(([cat]) => cat)
    );
    return sortedGroups;
  }, [filteredListings, groupByCategory]);

  // Handle infinite scroll
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !loadingMore) {
        logger.log('📜 Loading more listings...');
        const nextPage = pagination.page + 1;
        setPagination((prev) => ({ ...prev, page: nextPage }));
        loadListings(nextPage, debouncedSearch, sortBy, sortOrder, '', true);
      }
    },
    [hasMore, loadingMore, pagination.page, debouncedSearch, sortBy, sortOrder, loadListings]
  );

  if (collapsed) {
    return (
      <div className="homepage-sidebar collapsed">
        <div className="sidebar-header">
          <h2>🎨</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage-sidebar">
      <div className="sidebar-header">
        <div className="header-row">
          <h2>Select your Decals</h2>
          <div className="sticker-count">
            {filteredListings.length} stickers
            {selectedTags.length > 0 && ` (${listings.length} total)`}
          </div>
        </div>
      </div>

      {/* Modern Search and Controls */}
      <div className="sidebar-controls">
        {/* Search - Material Design */}
        <div className={`search-field ${filterText ? 'has-value' : ''}`}>
          <MaterialIcon icon="search" size={18} />
          <input
            ref={searchInputRef}
            type="text"
            value={filterText}
            onChange={handleSearch}
            className="search-input"
            id="sidebar-search-input"
            placeholder="Search stickers..."
          />
          {filterText && (
            <button
              type="button"
              onClick={() => {
                setFilterText('');
                if (searchInputRef.current) {
                  searchInputRef.current.value = '';
                }
              }}
              className="search-clear-btn"
              title="Clear search"
              aria-label="Clear search"
            >
              <MaterialIcon icon="close" size={16} />
            </button>
          )}
        </div>

        {/* Compact Controls Row */}
        <div className="controls-row">
          {/* Tag Filter */}
          <div className="tag-filter-compact">
            <TagFilter
              allTags={allTags}
              selectedTags={selectedTags}
              onTagsChange={handleTagsChange}
              maxTags={10}
            />
          </div>

          {/* Sort Controls */}
          <div className="sort-container">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="unified-control-button sort-button"
            >
              <MaterialIcon icon="sort" size={18} />
              <span className="sort-text">
                {sortBy === 'sales' ? 'Sales' : sortBy === 'title' ? 'Title' : 'Date'}
              </span>
              <MaterialIcon icon={showSortDropdown ? 'expand_less' : 'expand_more'} size={18} />
            </button>

            {showSortDropdown && (
              <div className="sort-dropdown">
                <button
                  className={`sort-option ${sortBy === 'sales' ? 'selected' : ''}`}
                  onClick={() => {
                    setSortBy('sales');
                    setShowSortDropdown(false);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                    loadListings(1, debouncedSearch, 'sales', sortOrder);
                  }}
                >
                  Sales
                </button>
                <button
                  className={`sort-option ${sortBy === 'title' ? 'selected' : ''}`}
                  onClick={() => {
                    setSortBy('title');
                    setShowSortDropdown(false);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                    loadListings(1, debouncedSearch, 'title', sortOrder);
                  }}
                >
                  Title
                </button>
                <button
                  className={`sort-option ${sortBy === 'created_at' ? 'selected' : ''}`}
                  onClick={() => {
                    setSortBy('created_at');
                    setShowSortDropdown(false);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                    loadListings(1, debouncedSearch, 'created_at', sortOrder);
                  }}
                >
                  Date Added
                </button>
              </div>
            )}
          </div>

          {/* Group Toggle - HIDDEN: not working and causes overlapping */}
          {/* <div className="group-container">
            <button 
              onClick={() => {
                const newValue = !userGroupByCategory;
                setUserGroupByCategory(newValue);
                // If no filters active, apply immediately
                if (filterText.trim() === '' && selectedTags.length === 0) {
                  setGroupByCategory(newValue);
                }
              }}
              className={`unified-control-button group-toggle-btn ${groupByCategory ? 'active' : ''}`}
              title={filterText.trim() !== '' || selectedTags.length > 0 ? 'Grouping disabled when filters are active' : ''}
            >
              <MaterialIcon icon={groupByCategory ? 'view_list' : 'view_module'} size={18} />
              <span className="group-text">
                {groupByCategory ? 'Ungroup' : 'Group'}
              </span>
            </button>
          </div> */}
        </div>

        {/* Selected Tags Display */}
        {selectedTags.length > 0 && (
          <div className="selected-tags-display">
            {selectedTags.map((tag) => (
              <TagChip
                key={tag}
                tag={tag}
                onRemove={() => handleTagsChange(selectedTags.filter((t) => t !== tag))}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="sidebar-content" onScroll={handleScroll}>
        {loading ? (
          <div className="loading-container">
            <ClothedEyeLoader size="medium" showTagline={true} message="Loading stickers..." />
          </div>
        ) : error ? (
          <div className="error-container">
            <p>❌ {error}</p>
            <button onClick={() => loadListings()} className="retry-btn">
              Retry
            </button>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">🦕</div>
            <h3>No stickers found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : groupByCategory && groupedListings ? (
          // Grouped view
          <div className="grouped-listings">
            {groupedListings.map(([category, categoryListings]) => (
              <div key={category} className="category-group">
                <h3 className="category-title">{category}</h3>
                <div className="compact-stickers-grid">
                  {categoryListings.map((listing) => (
                    <div
                      key={listing.etsyListingId}
                      className="sticker-card-wrapper"
                      onClick={() => handleCardClick(listing)}
                    >
                      <SuperSimpleStickerCard
                        listingId={listing.etsyListingId}
                        title={listing.title}
                        category={listing.category}
                        firstImageUrl={listing.first_image_url}
                        largestSizeImageUrl={listing.largest_size_image_url}
                        etsyUrl={listing.etsy_url}
                        isTrueScale={listing.isTrueScale}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Regular grid view
          <div className="compact-stickers-grid">
            {filteredListings.map((listing) => (
              <div
                key={listing.etsyListingId}
                className="sticker-card-wrapper"
                onClick={() => handleCardClick(listing)}
              >
                <SuperSimpleStickerCard
                  listingId={listing.etsyListingId}
                  title={listing.title}
                  category={listing.category}
                  firstImageUrl={listing.first_image_url}
                  largestSizeImageUrl={listing.largest_size_image_url}
                  etsyUrl={listing.etsy_url}
                  isTrueScale={listing.isTrueScale}
                />
              </div>
            ))}
          </div>
        )}

        {/* Loading More Indicator */}
        {loadingMore && (
          <div className="loading-more">
            <ClothedEyeLoader size="small" showTagline={false} message="Loading more stickers..." />
          </div>
        )}

        {/* End of All Results */}
        {!hasMore && listings.length > 0 && (
          <div className="end-of-all-results">
            <p>You've reached the end! All {listings.length} stickers loaded.</p>
          </div>
        )}
      </div>
    </div>
  );
};

HomePageSidebar.propTypes = {
  onAddToScene: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  onRefreshRef: PropTypes.object,
  onLoadingStateChange: PropTypes.func,
};

export default HomePageSidebar;
